/** Pinia Store para la gestión de Avisos de Privacidad relacionados con el Buró de Crédito. */
import { defineStore } from 'pinia';
import { getCurrentInstance } from 'vue';
import { useToast } from 'vue-toast-notification';
import { useLoadingStore } from '@/pinia/loading.module';
import { useAuthStore } from '@/pinia/auth.module';
import { getLocaleMessage } from '@/services/MessagesService';
import { getNestedValue } from '@/helpers/sort';
import * as service from '@/services';
import html2pdf from 'html2pdf.js';

const $toast = useToast();

export const useContractStore = defineStore('contract', {
    /**
     * Estado central del store.
     */
    state: () => ({
        /**Modal */
        isEdit: false,
        isModalOpen: false,
        loanApplicationsOptions: [],
        selectedApplication: null,
        contractContent: null,
        errors: null,
        /**Formulario */
        form: {},
        formSaveCOAS: {}, //Formulario: CONTRATO DE APERTURA DE CRÉDITO SIMPLE (PF y PM) || CONTRATO DE CREDITO CON GARANTÍA PRENDARIA (PF y PM) || Crédito simple con comisión e interés para tarjeta de crédito alias CONTRATO DE APERTURA DE CRÉDITO (TARJETA AMERICAN EXPRESS) - (PF Y PM)
        nameFilePDF: "Descarga",
        /**Datos de tabla */
        sortKey: '',
        sortOrder: 1, // 1 for ascending, -1 for descending
        itemsPerPage: 10, //Por default 10 por pagina
        currentPage: 1,
        searchString: '',
        productContract: []
    }),
    /**
     * Getters para filtrar, ordenar y paginar los avisos.
     */
    getters: {
        /**
         * Devuelve los contratos filtradas por el texto de búsqueda.
         */
        filteredContract: (state) => {
            if (!state.searchString) return state.productContract;

            //#region control de fecha string
            const instance = getCurrentInstance();
            const $filters = instance?.appContext.config.globalProperties.$filters;
            //#endregion control de fecha string

            const searchLower = state.searchString.toLowerCase();
            return state.productContract.filter(contract => {
                const firstNameProspect = contract.loanApplication?.prospectus?.generalInformation?.firstName ? contract.loanApplication?.prospectus?.generalInformation?.firstName?.toLowerCase() : '';
                const lastNameProspect = contract.loanApplication?.prospectus?.generalInformation?.lastName ? contract.loanApplication?.prospectus?.generalInformation?.lastName?.toLowerCase() : '';
                const mothersMaidenNameProspect = contract.loanApplication?.prospectus?.generalInformation?.mothersMaidenName ? contract.loanApplication?.prospectus?.generalInformation?.mothersMaidenName?.toLowerCase() : '';
                const nameProspect = contract.loanApplication?.prospectus?.generalInformation?.businessName === null ?
                    `${firstNameProspect} ${lastNameProspect} ${mothersMaidenNameProspect}`
                    :
                    contract.loanApplication?.prospectus?.generalInformation?.businessName ? contract.loanApplication?.prospectus?.generalInformation?.businessName?.toLowerCase() : '';

                return (
                    contract.referenceNumber?.toLowerCase().includes(searchLower) ||
                    contract.loanApplication?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    contract.loanApplication?.prospectus?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    contract.loanApplication?.prospectus?.quotations?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    $filters?.formatDate(contract.acknowledgedReceived)?.toLowerCase().includes(searchLower) ||
                    $filters?.formatDate(contract.acknowledgedKnown)?.toLowerCase().includes(searchLower) ||
                    nameProspect.includes(searchLower) ||
                    contract.loanApplication?.productType?.name?.toLowerCase().includes(searchLower) ||
                    contract.loanApplication?.prospectus?.personTypes?.name?.toLowerCase().includes(searchLower) ||
                    $filters?.formatDate(contract.createdAt)?.toLowerCase().includes(searchLower) ||
                    $filters?.formatDate(contract.updatedAt)?.toLowerCase().includes(searchLower)
                );
            });
        },
        /**
         * Ordena los contratos según la clave de orden actual.
         */
        sortedContract: (state) => {
            // Logica de ordenamiento
            let sortedList = [...state.filteredContract];
            if (state.sortKey) {
                sortedList.sort((a, b) => {

                    // Caso especial para el nombre
                    if (state.sortKey === 'name') {
                        const getNameForSort = (item) => {
                            if (!item) return '';

                            if (item.businessName === null) {
                                return `${item.firstName || ''} ${item.lastName || ''} ${item.mothersMaidenName || ''}`.toLowerCase();
                            } else {
                                return (item.businessName || '').toLowerCase();
                            }
                        };

                        const aName = getNameForSort(a);
                        const bName = getNameForSort(b);
                        if (aName < bName) return -1 * state.sortOrder;
                        if (aName > bName) return 1 * state.sortOrder;
                        return 0;
                    }

                    // Ordenamiento normal para otras columnas
                    let aValue = getNestedValue(a, state.sortKey);
                    let bValue = getNestedValue(b, state.sortKey);

                    if (aValue < bValue) return -1 * state.sortOrder;
                    if (aValue > bValue) return 1 * state.sortOrder;
                    return 0;
                });
            }
            return sortedList;
        },
        /**
         * Aplica paginación a la lista ordenada.
         */
        paginatedContracts: (state) => {
            if (!state.sortedContract || state.sortedContract.length === 0) return [];
            let start = (state.currentPage * state.itemsPerPage) - state.itemsPerPage;
            return state.sortedContract.slice(start, start + state.itemsPerPage);
        }
    },
    /**
     * Acciones asincrónicas y mutaciones del store.
     */
    actions: {
        /**
         * Obtiene las opciones de solicitudes de crédito disponibles para llenar el combo de selección de la modal de aviso de contrato.
         * http://localhost:5145/api/products/loanApplications/contract/1 ${userAuth.user.companyId}
         */
        async filterLoanApplications() {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.productsApi.get(`/api/products/loanApplications/contract/${useAuthStore().user.companyId}`);
                if (data && data.status) {
                    this.loanApplicationsOptions = data.data;
                }
                useLoadingStore().loading = false;
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Crea un nuevo regisro de contrato de producto.
         * @param {*} payload 
         * @returns 
         * http://localhost:5145/api/products/contract/branch/{branchOfficeId}/company/{companyId}
         */
        async createProductContract(payload) {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.productsApi.post(`/api/products/contract/branch/${this.selectedApplication.branchOfficesId}/company/${this.selectedApplication.companyId}`, payload);
                if (data && data.status) {
                    $toast.success(getLocaleMessage('productContract', 'creatingSuccess'));
                    this.formSaveCOAS.referenceNumber = data.data.referenceNumber;
                    data.data.loanApplication = this.selectedApplication;
                    this.productContract.unshift(data.data);
                    return data;
                } else {
                    $toast.success(getLocaleMessage('productContract', 'creatingError'));
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Obtiene todos los contratos registrados.
         * http://localhost:5145/api/products/contract/company/1
         */
        async fetchProductContract() {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.productsApi.get(`/api/products/contract/company/${useAuthStore().user.companyId}`);
                if (data && data.status) {
                    this.productContract = data.data;
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Actualiza un registro de contrato existente.
         * http://localhost:5145/api/products/contract
         */
        async updateProductContract(payload) {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.productsApi.put(`/api/products/contract`, payload);
                if (data && data.status) {
                    const index = this.productContract.findIndex(c => c.id === payload.id);
                    if (index !== -1) {
                        this.productContract.splice(index, 1, data.data);
                    }
                    $toast.success(getLocaleMessage('productContract', 'editingSuccess'));
                    return data;
                } else {
                    $toast.success(getLocaleMessage('productContract', 'editingError'));
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Abre el modal para crear nuevo registro de contrato.
         * Acción por el boton Generar
         * Abrir modal de creación isEdit = false
         */
        async openModal() {
            this.isModalOpen = true;
            this.isEdit = false;
            await this.filterLoanApplications(); //Refrescar los datos del combo de selección
        },
        /**
         * Cierra el modal y limpia estados relacionados.
         */
        async closeModal() {
            this.isEdit = false;
            this.isModalOpen = false;
            this.loanApplicationsOptions = [];
            this.selectedApplication = null;
            this.contractContent = null;
            this.errors = null;
            this.form = {};
            this.formSaveCOAS = {};
            this.nameFilePDF = "Descarga";
            await this.fetchProductContract(); //Refrescar datos de las tablas
        },
        /**
         * Maneja errores en la comunicación con la API.
         */
        handleError(error) {
            this.errors = error;
            if (error.response) {
                console.log("Inspec handle ERROR IF: ", error)
                let msg = error?.response?.data?.error || getLocaleMessage('common', 'responseError');
                $toast.error(msg);
            } else if (error.request) {
                console.log("Inspec handle ERROR ELSE IF: ", error)
                let msg = error?.response?.data?.error || getLocaleMessage('common', 'networkError');
                $toast.error(msg);
            } else {
                console.log("Inspec handle ERROR ELSE: ", error)
                let msg = error?.response?.data?.error || getLocaleMessage('common', 'generalError');
                $toast.error(msg);
            }
        },
        /**
        * Cambia el orden de las columnas en la tabla.
        */
        sortBy(key) {
            if (this.sortKey === key) {
                this.sortOrder *= -1;
            } else {
                this.sortOrder = 1;
            }
            this.sortKey = key;
            this.currentPage = 1; // Resetear a la primera página al ordenar
        },
        /**
         * Establece la página actual del paginador.
         */
        setCurrentPage(page) {
            this.currentPage = page;
        },
        /**
         * Carga los datos de un contrato existente para edición.
         * Abrir modal de edición isEdit = true
         */
        editContract(contract) {
            this.isEdit = true;
            this.selectedApplication = contract.loanApplication;
            this.formSaveCOAS.referenceNumber = contract.referenceNumber;
            this.formSaveCOAS.acknowledgedReceived = contract.acknowledgedReceived;
            this.formSaveCOAS.acknowledgedKnown = contract.acknowledgedKnown;
            this.formSaveCOAS.witnesses = contract.witnesses;
            this.formSaveCOAS.id = contract.id;
            this.formSaveCOAS.loanApplicationId = contract.loanApplicationId;
            this.formSaveCOAS.statusId = contract.statusId;
            this.formSaveCOAS.createdAt = contract.createdAt;
            this.formSaveCOAS.loanApplication = contract.loanApplication;
        },
        /**
         * Acción para guarda los datos del formulario, ya sea creando o actualizando.
         */
        async saveData() {
            try {
                useLoadingStore().loading = true;

                let response;
                if (this.isEdit) {
                    response = await this.updateProductContract(this.formSaveCOAS);
                } else {
                    response = await this.createProductContract(this.formSaveCOAS);
                }

                if (response && response.status) {
                    this.downloadPDF();
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Funcionalidad para manejar la descarga de un pdf
         * @returns Descarga un PDF
         */
        async downloadPDF() {
            if (!this.contractContent) return;

            /**Ocultar el boton de descarga */
            const formActions = document.querySelector('.form-actions');
            if (formActions) formActions.style.display = 'none';

            const element = document.querySelectorAll('.paper-sheet');
            if (element) {
                element.forEach(p => {
                    p.classList.add('pdf-mode');
                    p.style.marginBottom = 0;
                });
            }

            const opt = {
                //margin: [10, 25, 10, 25], // [top, left, bottom, right] en cm
                //margin: 0,
                filename: this.nameFilePDF,
                image: { type: 'jpeg', quality: 1 },//quality: 1 0.98
                html2canvas: {
                    scale: 2,
                    logging: true,
                    useCORS: true,
                    letterRendering: true,
                    clip: false, // Permite capturar contenido fuera de los límites del viewport
                    ignoreElements: function(element) {
                        return false; // Captura todos los elementos
                    }
                },
                jsPDF: {
                    unit: 'mm',
                    format: 'a4', // Tamaño A4 en píxeles
                    orientation: 'portrait'
                },
                pagebreak: {
                    mode: ['css'], //'avoid-all', 'legacy'
                    after: '.paper-sheet', //['.paper-sheet', '.content-firma'],  //Usar en elementos clave
                    //avoid: ['.declaration-item']
                }
            };

            try {
                useLoadingStore().loading = true;
                await html2pdf().set(opt).from(this.contractContent).save();
            } catch (error) {
                useLoadingStore().loading = false;
                this.handleError(error);
            } finally {
                useLoadingStore().loading = false;

                /**Restableciendo diseños de origen en la modal */
                if (formActions) formActions.style.display = 'block';

                if (element) {
                    element.forEach(p => {
                        p.classList.remove('pdf-mode');
                        p.style.marginBottom = '20px';
                    });
                }
            }

            /**
             * Nota: Con esta parte del codigo soluciono el detalle de cuando se mantiene
             * abierto la modal de creación despues dar descargar y este se manda a GUARDAR 
             * si el usuario quiere modificar algun campo y mandar descargar de nuevo con la
             * nueva captura en alguno de los campos no deja porque le marca error de solicitud
             * ya existente.
             * Opción 1:
             * Comportamiento: Una vez creado el registro y como se mantiene la modal abierta
             * por automatico cuando se termina de GUARDAR el registro en la BD este se convierte
             * en una modal de edición. Si ese es el comportamiento adecuado dejar este codigo
             * Opción 2:
             * Comportamiento: Una vez creado el registro y como se mantiene la modal abierta
             * y sigue siendo una creación este al querer editar un dato y de descargar lo sigue
             * reconociendo como una creación y marca error porque dice que ya existe el uso
             * de esa solicitud dentro del proceso de aviso de privacidad. Si ese es el
             * comportamiento adecuado retirar este codigo
             */
            //this.isEdit = true; //indica vuelvelo edición
            //this.policyId = this.privacyPolicies[0].id; //setea el valor del nuevo registro creado
            //this.numReference = this.privacyPolicies[0].loanApplication.referenceNumber; //setea el valor de numero de solicitud
        },
        setContractContent(ref) {
            this.contractContent = ref;
        }
    }
});